'use client';

import {
  Fragment,
  forwardRef,
  useMemo,
  useState,
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { cn } from '../../lib/cn';
import { withDisplayName } from '../../lib/display-name';
import { SCheckbox } from '../SCheckbox';
import { SGhostButton } from '../SGhostButton';

export interface STreeNode {
  /** 노드 고유 ID */
  id: string;
  /** 노드 라벨 */
  label: ReactNode;
  /** 하위 노드 */
  children?: STreeNode[];
  /** 비활성 상태 */
  disabled?: boolean;
}

export interface STreeItemRenderState {
  node: STreeNode;
  depth: number;
  expanded: boolean;
  selected: boolean;
  indeterminate: boolean;
  disabled: boolean;
  hasChildren: boolean;
}

export interface STreeItemProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'children' | 'title' | 'onSelect' | 'onToggle'
> {
  /** 렌더링할 노드 */
  node: STreeNode;
  /** 중첩 단계 */
  depth?: number;
  /** 마지막 자식 여부 */
  isLast?: boolean;
  /** 상위 depth 별 마지막 자식 여부 */
  ancestorLast?: boolean[];
  /** 펼침 상태 */
  expanded?: boolean;
  /** 선택 상태 */
  selected?: boolean;
  /** depth 연결선 표시 여부 */
  guideline?: boolean;
  /** 아이템 하단 border 표시 여부 */
  border?: boolean;
  /** 일부 선택 상태 */
  indeterminate?: boolean;
  /** 체크박스 표시 여부 */
  selectable?: boolean;
  /** 사용자 지정 leading 콘텐츠 */
  leading?: ReactNode | ((state: STreeItemRenderState) => ReactNode);
  /** 사용자 지정 trailing 콘텐츠 */
  trailing?: ReactNode | ((state: STreeItemRenderState) => ReactNode);
  /** 아이템 확장/축소 토글 */
  onToggle?: (node: STreeNode, event?: MouseEvent<HTMLElement>) => void;
  /** 선택 토글 */
  onTreeSelect?: (node: STreeNode, event?: MouseEvent<HTMLElement>) => void;
}

export interface STreeProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** 트리 노드 목록 */
  items: STreeNode[];
  /** 선택된 노드 ID 목록 */
  value?: string[];
  /** 비제어 선택 초기값 */
  defaultValue?: string[];
  /** 펼쳐진 노드 ID 목록 */
  expandedIds?: string[];
  /** 비제어 펼침 초기값 */
  defaultExpandedIds?: string[];
  /** 체크박스 표시 여부 */
  selectable?: boolean;
  /** depth 연결선 표시 여부 */
  guideline?: boolean;
  /** 아이템 하단 border 표시 여부 */
  border?: boolean;
  /** 부모 선택 시 하위 노드까지 함께 토글 */
  cascadeSelection?: boolean;
  /** 사용자 지정 leading 콘텐츠 */
  leading?: ReactNode | ((state: STreeItemRenderState) => ReactNode);
  /** 사용자 지정 trailing 콘텐츠 */
  trailing?: ReactNode | ((state: STreeItemRenderState) => ReactNode);
  /** 선택 변경 */
  onValueChange?: (value: string[], node: STreeNode) => void;
  /** 펼침 변경 */
  onExpandedChange?: (expandedIds: string[], node: STreeNode) => void;
}

function collectIds(nodes: STreeNode[]): string[] {
  return nodes.flatMap(node => [node.id, ...collectIds(node.children ?? [])]);
}

function collectSelectableIds(node: STreeNode): string[] {
  if (node.disabled) return [];
  return [node.id, ...(node.children ?? []).flatMap(collectSelectableIds)];
}

function toSelectedSet(value: string[] | undefined): Set<string> {
  return new Set(value ?? []);
}

interface TreeSelectionState {
  selected: boolean;
  indeterminate: boolean;
}

function getSelectionState(node: STreeNode, selectedSet: Set<string>): TreeSelectionState {
  if (selectedSet.has(node.id)) {
    return { selected: true, indeterminate: false };
  }

  const selectableChildren = (node.children ?? []).filter(
    child => collectSelectableIds(child).length > 0,
  );

  if (selectableChildren.length === 0) {
    return { selected: false, indeterminate: false };
  }

  const childStates: TreeSelectionState[] = selectableChildren.map(child =>
    getSelectionState(child, selectedSet),
  );
  const selectedCount = childStates.filter(state => state.selected).length;

  return {
    selected: selectedCount === childStates.length,
    indeterminate:
      childStates.some(state => state.indeterminate) ||
      (selectedCount > 0 && selectedCount < childStates.length),
  };
}

function TreeConnector({
  guideline,
  depth,
  hasChildren,
  isLast,
  ancestorLast,
}: {
  guideline: boolean;
  depth: number;
  hasChildren: boolean;
  isLast: boolean;
  ancestorLast: boolean[];
}) {
  if (depth <= 1) return null;
  const ancestorConnectors = ancestorLast.slice(0, -1);

  return (
    <span aria-hidden="true" className="flex self-stretch">
      {ancestorConnectors.map((last, index) => (
        <span
          key={index}
          className="relative block w-[28px] shrink-0"
          data-tree-connector-parent={last ? undefined : 'through'}
        >
          {guideline && !last && (
            <span className="absolute bottom-0 left-[6px] top-[-1px] w-px bg-(--cmp-tree-connector-line-color)" />
          )}
        </span>
      ))}
      <span className="relative block w-[20px] shrink-0">
        {guideline && !isLast && (
          <span className="absolute bottom-0 left-[6px] top-[-1px] w-px bg-(--cmp-tree-connector-line-color)" />
        )}
        {guideline && (
          <>
            <span className="absolute left-[6px] top-[-1px] h-[17px] w-px bg-(--cmp-tree-connector-line-color)" />
            <span
              data-tree-connector-branch={hasChildren ? 'parent' : 'leaf'}
              className={cn(
                'absolute left-[6px] top-[15px] h-px bg-(--cmp-tree-connector-line-color)',
                hasChildren ? 'w-[14px]' : 'w-[calc(14px+var(--cmp-tree-item-arrow-icon))]',
              )}
            />
          </>
        )}
      </span>
    </span>
  );
}

const STreeItemImpl = forwardRef<HTMLDivElement, STreeItemProps>(function STreeItem(
  {
    node,
    depth = 1,
    isLast = true,
    ancestorLast = [],
    expanded = false,
    selected = false,
    guideline = true,
    border = true,
    indeterminate = false,
    selectable = true,
    leading,
    trailing,
    onToggle,
    onTreeSelect,
    className,
    ...rest
  },
  ref,
) {
  const hasChildren = Boolean(node.children?.length);
  const renderState = {
    node,
    depth,
    expanded,
    selected,
    indeterminate,
    disabled: Boolean(node.disabled),
    hasChildren,
  };
  const leadingNode = typeof leading === 'function' ? leading(renderState) : leading;
  const trailingNode = typeof trailing === 'function' ? trailing(renderState) : trailing;
  const checkboxValue = indeterminate ? null : selected;

  const handleSelect = (event?: MouseEvent<HTMLElement>) => {
    if (node.disabled) return;
    onTreeSelect?.(node, event);
  };

  const handleRowClick = (event: MouseEvent<HTMLDivElement>) => {
    if (node.disabled) return;
    if (hasChildren) onToggle?.(node, event);
  };

  return (
    <div
      ref={ref}
      role="treeitem"
      aria-level={depth}
      aria-expanded={hasChildren ? expanded : undefined}
      aria-selected={selectable ? selected : undefined}
      aria-disabled={node.disabled || undefined}
      onClick={handleRowClick}
      className={cn(
        'group/tree-item flex min-h-[32px] w-full items-center gap-(--cmp-tree-item-rowGap) bg-(--cmp-tree-item-bg-default) px-(--cmp-tree-item-paddingX) text-left',
        border && 'border-b border-(--cmp-tree-item-border-default)',
        !node.disabled && 'hover:bg-(--cmp-tree-item-bg-hover)',
        node.disabled && 'cursor-not-allowed opacity-60',
        !node.disabled && hasChildren && 'cursor-pointer',
        className,
      )}
      {...rest}
    >
      <TreeConnector
        guideline={guideline}
        depth={depth}
        hasChildren={hasChildren}
        isLast={isLast}
        ancestorLast={ancestorLast}
      />
      <div className="flex min-w-0 flex-1 items-center gap-(--cmp-tree-item-contentGap) py-(--cmp-tree-item-paddingY)">
        {hasChildren ? (
          <SGhostButton
            icon={expanded ? 'caretDown' : 'caretRight'}
            size="xxs"
            ariaLabel={expanded ? 'collapse tree item' : 'expand tree item'}
            ariaExpanded={expanded}
            disabled={node.disabled}
            onClick={event => {
              event.stopPropagation();
              if (node.disabled) return;
              onToggle?.(node, event);
            }}
          />
        ) : (
          <span className="size-(--cmp-tree-item-arrow-icon) shrink-0" aria-hidden="true" />
        )}
        {leadingNode}
        {selectable && (
          <span
            onClick={event => event.stopPropagation()}
            className="flex shrink-0"
            aria-hidden={node.disabled ? 'true' : undefined}
          >
            <SCheckbox
              value={checkboxValue}
              disabled={node.disabled}
              onValueChange={() => handleSelect()}
            />
          </span>
        )}
        <span
          className={cn(
            'min-w-0 flex-1 text-[14px] leading-[24px]',
            selected || indeterminate
              ? 'font-bold text-(--cmp-tree-item-text-selected)'
              : 'font-medium text-(--cmp-tree-item-text-default)',
          )}
        >
          <span
            onClick={event => {
              if (!selectable) return;
              event.stopPropagation();
              if (!node.disabled && selectable) onTreeSelect?.(node, event);
            }}
            className={cn(
              'inline-block max-w-full truncate',
              node.disabled ? 'cursor-not-allowed' : selectable ? 'cursor-pointer' : undefined,
            )}
          >
            {node.label}
          </span>
        </span>
        {trailingNode}
      </div>
    </div>
  );
});

export const STreeItem = withDisplayName(STreeItemImpl, 'STreeItem');

const STreeImpl = forwardRef<HTMLDivElement, STreeProps>(function STree(
  {
    items,
    value,
    defaultValue,
    expandedIds,
    defaultExpandedIds,
    selectable = true,
    cascadeSelection = true,
    guideline = true,
    border = true,
    leading,
    trailing,
    onValueChange,
    onExpandedChange,
    className,
    ...rest
  },
  ref,
) {
  const [internalValue, setInternalValue] = useState<string[]>(defaultValue ?? []);
  const [internalExpandedIds, setInternalExpandedIds] = useState<string[]>(
    defaultExpandedIds ?? items.filter(item => item.children?.length).map(item => item.id),
  );
  const currentValue = value ?? internalValue;
  const currentExpandedIds = expandedIds ?? internalExpandedIds;
  const selectedSet = useMemo(() => toSelectedSet(currentValue), [currentValue]);
  const expandedSet = useMemo(() => new Set(currentExpandedIds), [currentExpandedIds]);

  const updateExpanded = (node: STreeNode) => {
    const next = new Set(currentExpandedIds);
    if (next.has(node.id)) next.delete(node.id);
    else next.add(node.id);
    const nextValue = Array.from(next);
    if (expandedIds === undefined) setInternalExpandedIds(nextValue);
    onExpandedChange?.(nextValue, node);
  };

  const updateSelected = (node: STreeNode) => {
    if (node.disabled) return;

    const targetIds = cascadeSelection ? collectSelectableIds(node) : [node.id];
    const everySelected = getSelectionState(node, selectedSet).selected;
    const next = new Set(selectedSet);
    targetIds.forEach(id => {
      if (everySelected) next.delete(id);
      else next.add(id);
    });
    const orderedIds = collectIds(items).filter(id => next.has(id));
    if (value === undefined) setInternalValue(orderedIds);
    onValueChange?.(orderedIds, node);
  };

  const renderNodes = (nodes: STreeNode[], depth = 1, ancestorLast: boolean[] = []): ReactNode =>
    nodes.map((node, index) => {
      const isLast = index === nodes.length - 1;
      const isExpanded = expandedSet.has(node.id);
      const { selected, indeterminate } = getSelectionState(node, selectedSet);

      return (
        <Fragment key={node.id}>
          <STreeItem
            node={node}
            depth={depth}
            isLast={isLast}
            ancestorLast={ancestorLast}
            expanded={isExpanded}
            selected={selected}
            indeterminate={indeterminate}
            selectable={selectable}
            guideline={guideline}
            border={border}
            leading={leading}
            trailing={trailing}
            onToggle={updateExpanded}
            onTreeSelect={updateSelected}
          />
          {node.children?.length ? (
            <div
              aria-hidden={!isExpanded}
              className={cn(
                'grid w-full overflow-hidden transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none',
                isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
              )}
            >
              <div className="min-h-0 overflow-hidden">
                {renderNodes(node.children, depth + 1, [...ancestorLast, isLast])}
              </div>
            </div>
          ) : null}
        </Fragment>
      );
    });

  return (
    <div
      ref={ref}
      role="tree"
      className={cn('flex w-full flex-col items-start bg-(--cmp-tree-item-bg-default)', className)}
      {...rest}
    >
      {renderNodes(items)}
    </div>
  );
});

export const STree = withDisplayName(STreeImpl, 'STree');
